import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { PlanningBudgetMonth, PlanningTableData } from './planningTableMapper';
import { formatCurrency } from './productCosts';

export interface PlanningExportSection {
	title: string;
	table: PlanningTableData;
}

export interface PlanningExportData {
	title?: string;
	fileName: string;
	info: Array<[string, string]>;
	sections: PlanningExportSection[];
}

const toMatrix = ({ columns, rows }: PlanningTableData) => ({
	headers: columns.map((column) => column.label),
	body: rows.map((row) => columns.map((column) => row.cells[column.key] ?? '')),
	totalIndexes: rows.flatMap((row, index) => (row.isTotal ? [index] : [])),
});

export const buildBudgetExportSections = (
	months: PlanningBudgetMonth[],
	tableByPeriod: (period: string) => PlanningTableData
): PlanningExportSection[] =>
	months.map((month) => ({
		title: `Orçamento ${month.label} - ${formatCurrency(month.total)}`,
		table: tableByPeriod(month.period),
	}));

const sheetName = (title: string, index: number) =>
	title.replace(/[\\/?*[\]:]/g, '-').slice(0, 28) || `Tabela ${index + 1}`;

export const exportPlanningToExcel = ({ fileName, info, sections }: PlanningExportData) => {
	const wb = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(info), 'Planejamento');

	sections.forEach((section, index) => {
		const { headers, body } = toMatrix(section.table);
		const ws = XLSX.utils.aoa_to_sheet([[section.title], [], headers, ...body]);
		ws['!cols'] = headers.map((_header, column) => ({ wch: column === 0 ? 24 : 18 }));
		XLSX.utils.book_append_sheet(wb, ws, sheetName(section.title, index));
	});

	XLSX.writeFile(wb, `${fileName}.xlsx`);
};

export const exportPlanningToPdf = ({
	title = 'Planejamento',
	fileName,
	info,
	sections,
}: PlanningExportData) => {
	const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

	doc.setFontSize(16);
	doc.text(title, 14, 15);

	doc.setFontSize(10);
	let y = 24;
	for (const [label, value] of info) {
		doc.setFont('helvetica', 'bold');
		doc.text(`${label}: `, 14, y);
		const labelWidth = doc.getTextWidth(`${label}: `);
		doc.setFont('helvetica', 'normal');
		doc.text(value, 14 + labelWidth, y);
		y += 6;
	}

	sections.forEach((section) => {
		const { headers, body, totalIndexes } = toMatrix(section.table);
		doc.setFont('helvetica', 'bold');
		doc.setFontSize(11);
		doc.text(section.title, 14, y + 6);
		autoTable(doc, {
			startY: y + 9,
			head: [headers],
			body,
			styles: { fontSize: 8, cellPadding: 2 },
			headStyles: { fillColor: [90, 106, 207], textColor: 255, fontStyle: 'bold' },
			alternateRowStyles: { fillColor: [245, 245, 245] },
			didParseCell: (data) => {
				if (data.section === 'body' && totalIndexes.includes(data.row.index)) {
					data.cell.styles.fontStyle = 'bold';
					data.cell.styles.fillColor = [230, 230, 230];
				}
			},
		});
		y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 4;
	});

	doc.save(`${fileName}.pdf`);
};
